import { fabric } from "fabric";
import { getCurrentCanvas } from "../../../helpers/helper";

let aligningLineOffset = 3,
  aligningLineMargin = 3,
  aligningLineWidth = 1,
  aligningLineColor = "rgb(60,60,60)",
  viewportTransform,
  zoom = 1;

let verticalLines = [],
  horizontalLines = [];

function drawLine(x1, y1, x2, y2) {
  const ctx = getCurrentCanvas()?.getSelectionContext();
  if (!ctx) return;
  ctx.save();
  ctx.lineWidth = aligningLineWidth;
  ctx.strokeStyle = aligningLineColor;
  ctx.beginPath();
  ctx.moveTo(x1 * zoom + viewportTransform[4], y1 * zoom + viewportTransform[5]);
  ctx.lineTo(x2 * zoom + viewportTransform[4], y2 * zoom + viewportTransform[5]);
  ctx.lineDashOffset = 10;
  ctx.getLineDash();
  ctx.stroke();
  ctx.restore();
}

function drawVerticalLine(coords) {
  drawLine(coords.x + 0.5, coords.y1 > coords.y2 ? coords.y2 : coords.y1, coords.x + 0.5, coords.y2 > coords.y1 ? coords.y2 : coords.y1);
}

function drawHorizontalLine(coords) {
  drawLine(coords.x1 > coords.x2 ? coords.x2 : coords.x1, coords.y + 0.5, coords.x2 > coords.x1 ? coords.x2 : coords.x1, coords.y + 0.5);
}

function isInRange(value1, value2) {
  value1 = Math.round(value1);
  value2 = Math.round(value2);
  for (let i = value1 - aligningLineMargin, len = value1 + aligningLineMargin; i <= len; i++) {
    if (i === value2) {
      return true;
    }
  }
  return false;
}

function mouseChangeCanvasHandler(canvas) {
  canvas.on("mouse:down", function () {
    viewportTransform = canvas.viewportTransform;
    zoom = canvas.getZoom();
  });

  canvas.on("mouse:up", function () {
    verticalLines.length = horizontalLines.length = 0;
    canvas.renderAll();
  });
}

function objectModifyCanvasHandler(canvas) {
  const processSnap = (condition, snapDetails, positionFn) => {
    if (condition) {
      snapDetails.push(positionFn());
      return true;
    }
    return false;
  };

  const getVerticalLineDetails = (objectLeft, objectTop, activeObjectTop, objectHeight, activeObjectHeight) => ({
    x: objectLeft,
    y1: objectTop < activeObjectTop ? objectTop - objectHeight / 2 - aligningLineOffset : objectTop + objectHeight / 2 + aligningLineOffset,
    y2: activeObjectTop > objectTop ? activeObjectTop + activeObjectHeight / 2 + aligningLineOffset : activeObjectTop - activeObjectHeight / 2 - aligningLineOffset,
  });

  const getHorizontalLineDetails = (objectTop, objectLeft, activeObjectLeft, objectWidth, activeObjectWidth) => ({
    y: objectTop,
    x1: objectLeft < activeObjectLeft ? objectLeft - objectWidth / 2 - aligningLineOffset : objectLeft + objectWidth / 2 + aligningLineOffset,
    x2: activeObjectLeft > objectLeft ? activeObjectLeft + activeObjectWidth / 2 + aligningLineOffset : activeObjectLeft - activeObjectWidth / 2 - aligningLineOffset,
  });

  const updateObjectPosition = (activeObject, newPoint) => {
    activeObject.setPositionByOrigin(newPoint, "center", "center");
  };

  canvas.on("object:moving", function (e) {
    let activeObject = e.target,
      canvasObjects = canvas.getObjects(),
      activeObjectCenter = activeObject.getCenterPoint(),
      activeObjectLeft = activeObjectCenter.x,
      activeObjectTop = activeObjectCenter.y,
      activeObjectBoundingRect = activeObject.getBoundingRect(),
      activeObjectHeight = activeObjectBoundingRect.height / viewportTransform[3],
      activeObjectWidth = activeObjectBoundingRect.width / viewportTransform[0],
      horizontalInTheRange = false,
      verticalInTheRange = false,
      transform = canvas._currentTransform;

    if (!transform) return;

    for (let i = canvasObjects.length; i--; ) {
      if (canvasObjects[i] === activeObject) continue;

      let objectCenter = canvasObjects[i].getCenterPoint(),
        objectLeft = objectCenter.x,
        objectTop = objectCenter.y,
        objectBoundingRect = canvasObjects[i].getBoundingRect(),
        objectHeight = objectBoundingRect.height / viewportTransform[3],
        objectWidth = objectBoundingRect.width / viewportTransform[0];

      // Snap by the horizontal center line
      verticalInTheRange = processSnap(
        isInRange(objectLeft, activeObjectLeft),
        verticalLines,
        () => getVerticalLineDetails(objectLeft, objectTop, activeObjectTop, objectHeight, activeObjectHeight)
      ) || verticalInTheRange;
      if (verticalInTheRange) {
        updateObjectPosition(activeObject, new fabric.Point(objectLeft, activeObjectTop));
      }

      // Snap by the vertical center line
      horizontalInTheRange = processSnap(
        isInRange(objectTop, activeObjectTop),
        horizontalLines,
        () => getHorizontalLineDetails(objectTop, objectLeft, activeObjectLeft, objectWidth, activeObjectWidth)
      ) || horizontalInTheRange;
      if (horizontalInTheRange) {
        updateObjectPosition(activeObject, new fabric.Point(activeObjectLeft, objectTop));
      }
    }

    if (!horizontalInTheRange) {
      horizontalLines.length = 0;
    }

    if (!verticalInTheRange) {
      verticalLines.length = 0;
    }
  });
}

function canvasRenderHandler(canvas) {
  canvas.on("before:render", function () {
    if (canvas.contextTop) canvas.clearContext(canvas.contextTop);
  });

  canvas.on("after:render", function () {
    for (let i = verticalLines.length; i--; ) {
      drawVerticalLine(verticalLines[i]);
    }
    for (let i = horizontalLines.length; i--; ) {
      drawHorizontalLine(horizontalLines[i]);
    }

    verticalLines.length = horizontalLines.length = 0;
  });
}

/**
 * Should objects be aligned by a bounding box?
 * [Bug] Scaled objects sometimes can not be aligned by edges
 *
 */
export function initAligningGuidelines() {
  const canvas = getCurrentCanvas();
  if (!canvas) return;
  mouseChangeCanvasHandler(canvas);
  objectModifyCanvasHandler(canvas);
  canvasRenderHandler(canvas);
}
